import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { EditorBookingItem } from '../editor-booking-item/editor-booking-item.model';
import { EditorBookingItemSetting } from '../editor-booking-item-setting/editor-booking-item-setting.model';
import { EditorBookingItemMetadata } from '../editor-booking-item-metadata/editor-booking-item-metadata.model';
import { EditorBookingItemTreeStore } from '../editor-booking-item-tree/editor-booking-item-tree.store';
import { EditorDataService } from '../editor-data/editor-data.service';


export interface PatchingEditorBookingItemFilter {
  name: 'name'|'allButName'|'userBookingSettings'|'bundle'|'settings'|'metadataList';
  value: number;
}


export class PatchingEditorBookingItemMask {
  name: boolean;
  canBeBooked: boolean;
  hideSpecificBookingSettings: boolean;
  bundle: boolean;
  settings: number[];
  metadataList: number[];

  constructor(obj?: any) {
    this.name = obj?.name ?? false;
    this.canBeBooked = obj?.canBeBooked ?? false;
    this.hideSpecificBookingSettings = obj?.hideSpecificBookingSettings ?? false;
    this.bundle = obj?.bundle ?? false;
    this.settings = obj?.settings ?? null;
    this.metadataList = obj?.metadataList ?? null;
  }

  static getFromPatchingItemFilter(item: EditorBookingItem, itemFilter: PatchingEditorBookingItemFilter): PatchingEditorBookingItemMask {
    const name = itemFilter === null
      || itemFilter.name === 'name';

    const canBeBooked = itemFilter === null
      || itemFilter.name === 'allButName'
      || itemFilter.name === 'userBookingSettings';

    const hideSpecificBookingSettings = itemFilter === null
      || itemFilter.name === 'allButName'
      || itemFilter.name === 'userBookingSettings';

    const bundle = itemFilter === null
      || itemFilter.name === 'allButName'
      || itemFilter.name === 'bundle';

    let itemMaskSettings: number[];

    if (
      itemFilter === null
      || itemFilter.name === 'allButName'
      || (itemFilter.name === 'settings' && itemFilter.value === null)
    ) {
      itemMaskSettings = Array.from(item.settings.keys());
    } else if (itemFilter.name === 'settings') {
      itemMaskSettings = [itemFilter.value];
    } else {
      itemMaskSettings = null;
    }

    let itemMetadataList: number[];

    itemMetadataList = (itemFilter === null
      || itemFilter.name === 'allButName'
      || (itemFilter.name === 'metadataList')
    ) ? Array.from(item.metadataList.keys()) : null;

    return new this({
      name,
      canBeBooked,
      hideSpecificBookingSettings,
      bundle,
      settings: itemMaskSettings,
      metadataList: itemMetadataList
    });
  }
}


@Injectable()
export class EditorBookingItemEditStore {
  private readonly _replacingItem$ = new BehaviorSubject<EditorBookingItem>(null);
  private readonly _patchingItemFilter$ = new BehaviorSubject<PatchingEditorBookingItemFilter>(null);
  private readonly _patchingItemMask$ = new BehaviorSubject<PatchingEditorBookingItemMask>(null);
  private readonly _patchingItem$ = new BehaviorSubject<EditorBookingItem>(null);
  private readonly _parentPatchingItem$ = new BehaviorSubject<EditorBookingItem>(null);
  private readonly _patchingItemForm$ = new BehaviorSubject<FormGroup>(null);

  readonly replacingItem$ = this._replacingItem$.asObservable();
  readonly patchingItemFilter$ = this._patchingItemFilter$.asObservable();
  readonly patchingItem$ = this._patchingItem$.asObservable();
  readonly parentPatchingItem$ = this._parentPatchingItem$.asObservable();
  readonly patchingItemForm$ = this._patchingItemForm$.asObservable();

  constructor(
    private fb: FormBuilder,
    private itemTreeStore: EditorBookingItemTreeStore,
    private editorData: EditorDataService
  ) {
    combineLatest(
      this.itemTreeStore.editingItem$,
      this.itemTreeStore.parentEditingItem$,
      this.patchingItemFilter$
    )
    .pipe(
      filter(
        ([editingItem, _parentEditingItem, _patchingItemFilter]) =>
          editingItem !== null
      )
    )
    .subscribe(
      ([editingItem, parentEditingItem, patchingItemFilter]) => {
        this.initialize(
          editingItem,
          parentEditingItem,
          patchingItemFilter
        );
      }
    );

    this.itemTreeStore.editingItem$
    .pipe(
      map(
        (editingItem) =>
          editingItem !== null
      ),
      distinctUntilChanged(),
      filter(
        isEditing => !isEditing
      )
    )
    .subscribe(
      _ => this.terminate()
    );
  }

  private get replacingItem(): EditorBookingItem {
    return this._replacingItem$.getValue();
  }

  private set replacingItem(value: EditorBookingItem) {
    this._replacingItem$.next(value);
  }

  private get patchingItemFilter(): PatchingEditorBookingItemFilter {
    return this._patchingItemFilter$.getValue();
  }

  private set patchingItemFilter(value: PatchingEditorBookingItemFilter) {
    this._patchingItemFilter$.next(value);
  }

  private get patchingItemMask(): PatchingEditorBookingItemMask {
    return this._patchingItemMask$.getValue();
  }

  private set patchingItemMask(value: PatchingEditorBookingItemMask) {
    this._patchingItemMask$.next(value);
  }

  private get patchingItem(): EditorBookingItem {
    return this._patchingItem$.getValue();
  }

  private set patchingItem(value: EditorBookingItem) {
    this._patchingItem$.next(value);
  }

  private get parentPatchingItem(): EditorBookingItem {
    return this._parentPatchingItem$.getValue();
  }

  private set parentPatchingItem(value: EditorBookingItem) {
    this._parentPatchingItem$.next(value);
  }

  private get patchingItemForm(): FormGroup {
    return this._patchingItemForm$.getValue();
  }

  private set patchingItemForm(value: FormGroup) {
    this._patchingItemForm$.next(value);
  }

  initialize(item: EditorBookingItem, parentItem: EditorBookingItem, patchingItemFilter: PatchingEditorBookingItemFilter) {
    this.replacingItem = item;

    if (parentItem !== null) {
      const patchingParentItemMask = PatchingEditorBookingItemMask.getFromPatchingItemFilter(parentItem, patchingItemFilter);
      this.parentPatchingItem = this.getPatchingItem(parentItem, patchingParentItemMask);
    } else {
      this.parentPatchingItem = null;
    }

    const patchingItemMask = PatchingEditorBookingItemMask.getFromPatchingItemFilter(item, patchingItemFilter);

    this.patchingItemMask = patchingItemMask;
    this.patchingItemForm = this.buildItemForm(item, patchingItemMask);
    this.patchingItem = this.getPatchingItem(item, patchingItemMask);
  }

  terminate() {
    this.patchingItem = null;
    this.patchingItemForm = null;
    this.patchingItemMask = null;
    this.parentPatchingItem = null;
    this.replacingItem = null;
  }

  startEditing(itemId: string, patchingItemFilter: PatchingEditorBookingItemFilter) {
    this.patchingItemFilter = patchingItemFilter;
    this.itemTreeStore.startEditingItem(itemId);
  }

  stopEditing() {
    this.itemTreeStore.stopEditingItem();
    this.patchingItemFilter = null;
  }

  save(itemId: string) {
    const patchingItem = this.patchingItem;
    const patchingItemForm = this.patchingItemForm;
    const patchingItemMask = this.patchingItemMask;

    const replacingItem = this.replacingItem.createCopy();

    if (patchingItemMask.name) {
      if (patchingItemForm.get('name').dirty) {
        replacingItem.name = patchingItemForm.value.name;
      }
    }

    if (patchingItemMask.canBeBooked) {
      const canBeBookedForm = patchingItemForm.get('canBeBooked');
      if (canBeBookedForm !== null && canBeBookedForm.enabled) {
        replacingItem.canBeBooked = patchingItemForm.value.canBeBooked;
      }
    }

    if (patchingItemMask.hideSpecificBookingSettings) {
      const hideSpecificBookingSettingsForm = patchingItemForm.get('hideSpecificBookingSettings');
      if (hideSpecificBookingSettingsForm !== null && hideSpecificBookingSettingsForm.enabled) {
        replacingItem.hideSpecificBookingSettings = patchingItemForm.value.hideSpecificBookingSettings;
      }
    }

    if (patchingItemMask.bundle) {
      const bundleForm = patchingItemForm.get('bundle');
      if (bundleForm !== null && bundleForm.dirty) {
        replacingItem.bundle = patchingItemForm.value.bundle;
      }
    }

    if (patchingItemMask.settings !== null) {
      const settingsForm = patchingItemForm.get('settings') as FormArray;

      patchingItemMask.settings.forEach((replacingItemSettingIndex, patchingItemMaskSettingIndex) => {
        const patchingItemSettingForm = settingsForm.at(patchingItemMaskSettingIndex);
        const replacingItemSetting = replacingItem.settings[replacingItemSettingIndex];

        if (patchingItemSettingForm.dirty) {
          replacingItemSetting.applyFormValues(patchingItemSettingForm.value);
        }

        replacingItemSetting.inheritableType = patchingItem.settings[patchingItemMaskSettingIndex].inheritableType;
        if (replacingItemSetting.inheritableType === 'empty') {
          replacingItemSetting.applyFormValues(replacingItemSetting.getEmptyFormValues());
        }
      });
    }

    if (patchingItemMask.metadataList !== null) {
      const newMetadataList: EditorBookingItemMetadata[] = [];
      const metadataListForm = patchingItemForm.get('metadataList') as FormArray;

      patchingItemMask.metadataList.forEach((replacingItemMetadataIndex, patchingItemMaskMetadataIndex) => {
        const patchingItemMetadataForm = metadataListForm.at(patchingItemMaskMetadataIndex);
        const replacingItemMetadata = replacingItem.metadataList[replacingItemMetadataIndex];

        if (patchingItemMetadataForm.dirty) {
          replacingItemMetadata.value = patchingItemMetadataForm.value.value;
        }

        replacingItemMetadata.inheritableType = patchingItem.metadataList[patchingItemMaskMetadataIndex].inheritableType;
        if (replacingItemMetadata.inheritableType !== 'empty') {
          newMetadataList.push(replacingItemMetadata);
        }
      });

      replacingItem.metadataList = newMetadataList.concat(
        patchingItemForm.value.newMetadataList.map(newMetadataValue => new EditorBookingItemMetadata({
          inheritableType: 'set',
          name: newMetadataValue.name,
          value: newMetadataValue.value
        })).filter(newMetadata => !!newMetadata.name)
      );
    }

    this.itemTreeStore.replaceItem(itemId, replacingItem);
  }

  private getPatchingItem(item: EditorBookingItem, patchingItemMask: PatchingEditorBookingItemMask): EditorBookingItem {
    const patchingItem = item.createCopy();

    const patchingItemSettings: EditorBookingItemSetting[] = [];
    if (patchingItemMask.settings !== null) {
      patchingItemMask.settings.map(patchingItemMaskSetting =>
        patchingItemSettings.push(patchingItem.settings[patchingItemMaskSetting])
      );
    }
    patchingItem.settings = patchingItemSettings;

    const patchingItemMetadataList: EditorBookingItemMetadata[] = [];
    if (patchingItemMask.metadataList !== null) {
      patchingItemMask.metadataList.map(patchingItemMaskMetadata =>
        patchingItemMetadataList.push(patchingItem.metadataList[patchingItemMaskMetadata])
      );
    }
    patchingItem.metadataList = patchingItemMetadataList;

    return patchingItem;
  }

  private buildItemForm(item: EditorBookingItem, patchingItemMask: PatchingEditorBookingItemMask): FormGroup {
    const controlsConfig: any = {};

    if (patchingItemMask.name) {
      controlsConfig.name = this.fb.control(item.name);
    }

    if (patchingItemMask.canBeBooked) {
      const canBeBookedIsEditable = item.canBeBookedIsEditable();
      controlsConfig.canBeBooked = this.fb.control({
        value: canBeBookedIsEditable ? item.canBeBooked : false,
        disabled: !canBeBookedIsEditable
      });
    }

    if (patchingItemMask.hideSpecificBookingSettings) {
      controlsConfig.hideSpecificBookingSettings = this.fb.control(item.hideSpecificBookingSettings);
    }

    if (item.bundleIsEditable() && patchingItemMask.bundle) {
      controlsConfig.bundle = this.fb.control(item.bundle);
    }

    if (patchingItemMask.settings !== null) {
      const itemSettings: EditorBookingItemSetting[] = [];
      patchingItemMask.settings.map(patchingItemMaskSetting =>
        itemSettings.push(item.settings[patchingItemMaskSetting])
      );

      controlsConfig.settings = this.fb.array(itemSettings.map(
        itemSetting => this.buildItemSettingForm(itemSetting)
      ));
    }

    if (patchingItemMask.metadataList !== null) {
      const itemMetadataList: EditorBookingItemMetadata[] = [];
      patchingItemMask.metadataList.map(patchingItemMaskMetadata =>
        itemMetadataList.push(item.metadataList[patchingItemMaskMetadata])
      );

      controlsConfig.metadataList = this.fb.array(itemMetadataList.map(
        metadata => this.buildItemMetadataForm(metadata)
      ));

      controlsConfig.newMetadataList = this.fb.array([]);
    }

    return this.fb.group(controlsConfig);
  }

  private buildItemSettingForm(itemSetting: EditorBookingItemSetting): FormGroup {
    const formConfig = itemSetting.getFormConfig(this.editorData);
    return this.fb.group(formConfig.controlsConfig, formConfig.options);
  }

  private buildItemMetadataForm(itemMetadata: EditorBookingItemMetadata): FormGroup {
    return this.fb.group({ value: [itemMetadata.value] });
  }

  buildItemNewMetadataForm(): FormGroup {
    return this.fb.group({ name: [''], value: [''] });
  }
}
